import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Button,
  CircularProgress,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import SportsBasketballRoundedIcon from '@mui/icons-material/SportsBasketballRounded';

import api from '../../api/axiosConfig';
import { useInstatDesign } from './theme/instatDesign';
import { GlassHeader, SearchBar } from './ui';
import InstatPlayerView from './InstatPlayerView';

const DEFAULT_URL = 'https://app.hudl.com/instat/basketball/players/3260';
const HISTORY_KEY = 'instatpoc.history.v1';
const MAX_HISTORY = 10;

function parseInstatId(input) {
  if (!input) return null;
  const t = String(input).trim();
  if (/^\d+$/.test(t)) return Number(t);
  const m = t.match(/players\/(\d+)/);
  return m ? Number(m[1]) : null;
}

function loadJson(key, fallback) {
  try {
    const s = localStorage.getItem(key);
    return s ? JSON.parse(s) : fallback;
  } catch {
    return fallback;
  }
}
function saveJson(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

export default function InstatPOC() {
  const d = useInstatDesign();

  const [url, setUrl] = useState(DEFAULT_URL);
  const [playerId, setPlayerId] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [history, setHistory] = useState(() => loadJson(HISTORY_KEY, []));
  const [historyAnchor, setHistoryAnchor] = useState(null);
  const [error, setError] = useState(null);

  const loadPlayer = (input) => {
    const id = parseInstatId(input);
    if (!id) {
      setError('Invalid URL — expected `.../players/<id>` or a numeric id');
      return;
    }
    setError(null);
    setPlayerId(id);

    // Async fetch overview for history sidebar entry (name + team + photo).
    // Best-effort; history is local-only and we don't want to block the view.
    api.get(`/api/instat/player/${id}/overview`)
      .then(({ data: ov }) => {
        setHistory((prev) => {
          const entry = {
            id,
            name: ov?.info?.name || `Player ${id}`,
            team: ov?.info?.team?.name,
            photo: ov?.info?.photo,
            ts: Date.now(),
          };
          const next = [entry, ...prev.filter((p) => p.id !== id)].slice(0, MAX_HISTORY);
          saveJson(HISTORY_KEY, next);
          return next;
        });
      })
      .catch(() => null);
  };

  const resetAuth = async () => {
    setResetting(true);
    setError(null);
    try {
      await api.post('/api/instat/auth/reset');
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setResetting(false);
    }
  };

  const loadPlayerFromHistory = (entry) => {
    setUrl(`https://app.hudl.com/instat/basketball/players/${entry.id}`);
    setHistoryAnchor(null);
    setPlayerId(entry.id);
  };

  // Auto-load default player on mount so the page isn't empty.
  useEffect(() => {
    loadPlayer(DEFAULT_URL);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: d.colors.canvas,
        fontFamily: d.typo.fontFamily,
        color: d.colors.text,
        pb: 6,
      }}
    >
      <GlassHeader stickyTop={0}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ md: 'center' }}
          spacing={1.5}
          sx={{ maxWidth: 1400, mx: 'auto' }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                width: 36, height: 36, borderRadius: `${d.radius.md}px`,
                background: d.accentGradient,
                color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: d.shadows.card,
              }}
            >
              <SportsBasketballRoundedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ ...d.typo.title, lineHeight: 1.1 }}>Scouting</Typography>
              <Typography sx={{ ...d.typo.caption, lineHeight: 1 }}>
                Instat · Hudl data
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ flex: 1 }} />

          <SearchBar
            value={url}
            onChange={setUrl}
            onSubmit={() => loadPlayer(url)}
            placeholder="Hudl URL or player id"
            sx={{ maxWidth: 440, width: { xs: '100%', md: 380 } }}
          />

          <Button
            onClick={() => loadPlayer(url)}
            variant="contained"
            sx={{
              borderRadius: `${d.radius.pill}px`,
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              background: d.accentGradient,
              boxShadow: d.shadows.card,
              '&:hover': { background: d.accentGradient, boxShadow: d.shadows.cardHover, transform: 'translateY(-1px)' },
            }}
          >
            Load
          </Button>

          <Tooltip title="Recent players">
            <span>
              <IconButton
                onClick={(e) => setHistoryAnchor(e.currentTarget)}
                disabled={history.length === 0}
                sx={{ color: d.colors.textSecondary }}
              >
                <HistoryRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Force new Hudl login">
            <span>
              <IconButton onClick={resetAuth} disabled={resetting} sx={{ color: d.colors.textSecondary }}>
                {resetting ? <CircularProgress size={18} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>

          <Menu
            anchorEl={historyAnchor}
            open={Boolean(historyAnchor)}
            onClose={() => setHistoryAnchor(null)}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 280,
                borderRadius: `${d.radius.lg}px`,
                boxShadow: d.shadows.cardStrong,
                border: `1px solid ${d.colors.divider}`,
              },
            }}
          >
            {history.length > 0 && (
              <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
                <Typography sx={{ ...d.typo.micro, color: d.colors.textSecondary }}>Recent</Typography>
              </Box>
            )}
            {history.map((h) => (
              <MenuItem
                key={`hist-${h.id}`}
                onClick={() => loadPlayerFromHistory(h)}
                sx={{ gap: 1.25, py: 1 }}
              >
                <HistoryRoundedIcon sx={{ fontSize: 16, color: d.colors.textTertiary }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600 }}>{h.name}</Typography>
                  <Typography noWrap sx={{ ...d.typo.caption }}>{h.team || '—'}</Typography>
                </Box>
              </MenuItem>
            ))}
          </Menu>
        </Stack>
      </GlassHeader>

      <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, md: 3 }, pt: 3 }}>
        {error && (
          <Box sx={{ mb: 2, color: '#B91C1C', fontSize: 14 }}>{error}</Box>
        )}
        <InstatPlayerView
          instatPlayerId={playerId}
          embedded={false}
          stickyTop={72}
        />
      </Box>
    </Box>
  );
}
